import { useEffect, useState } from "react";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import useDebounce from "~/hooks/useDebounce";
import { z } from "zod";
function useUsers() {
  const roleList = [
    "GXM_ADMIN,GXM_USER,GXM_MANAGER",
    "LSP_USER,LSP_MANAGER",
    "CUSTOMER,CUSTOMER_MANAGER",
  ];
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState(0);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUserStatus, setDeleteUserStatus] = useState({
    toasterVisible: false,
    deleted: false,
    loading: false,
  });
  const debouncedSearchTerm = useDebounce(search, 500);
  const fetchUsers = async ({
    pageIndex = page,
    pageSize = rows,
    searchValue = search,
    entity = "",
    service = "",
  } = {}) => {
    setLoading(true);
    setIsDataAvailable(true);
    const { data } = await apiService(API_ROUTES.FETCH_USERS, "GET", {
      replace: {
        page: pageIndex + 1,
        pageSize,
        role: roleList[role],
        searchValue,
        entity,
        service,
      },
      schema: z.object({
        page: z.number().optional(),
        pageSize: z.number().optional(),
        role: z.string().optional(),
        search: z.string().optional(),
        entity: z.string().optional(),
        service: z.string().optional(),
      }),
    });
    const usersList = data?.users || [];
    setTotalRecords(data?.total_users || 0);
    if (usersList.length) {
      setUsers(usersList);
      setIsDataAvailable(true);
    } else {
      setUsers([]);
      setIsDataAvailable(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    setPage(0);
    fetchUsers({ pageIndex: 0 });
  }, [debouncedSearchTerm, role]);
  useEffect(() => {
    setSearch("");
    setRows(10);
    setTotalRecords(0);
    setDeleteUserStatus({
      toasterVisible: false,
      deleted: false,
      loading: false,
    });
  }, [role]);
  const handleChangePage = (e) => {
    setPage(e.page);
    setRows(e.rows);
    fetchUsers({ pageIndex: e.page, pageSize: e.rows });
  };
  const handleDelete = async (id) => {
    setDeleteUserStatus({
      toasterVisible: false,
      deleted: false,
      loading: true,
    });
    try {
      const res = await apiService(API_ROUTES.DELETE_USER, "DELETE", {
        replace: {
          id,
        },
        schema: z.object({
          id: z.string().uuid(),
        }),
      });
      if (res?.status) {
        setDeleteUserStatus({
          loading: false,
          toasterVisible: true,
          deleted: true,
        });
      }
      setOpenDialog(false);
      fetchUsers();
    } catch (error) {
      setDeleteUserStatus({
        loading: false,
        toasterVisible: false,
        status: false,
      });
      setOpenDialog(false);
    }
  };
  const handleOpenDialog = (detail) => {
    setSelectedUser(detail);
    setOpenDialog(true);
  };

  return {
    users,
    loading,
    searchQuery: search,
    setSearchQuery: setSearch,
    role,
    setRole,
    page,
    rows,
    totalRecords,
    onPageChange: handleChangePage,
    openDialog,
    setOpenDialog,
    handleOpenDialog,
    selectedUser,
    handleDelete,
    deleteUserStatus,
    setDeleteUserStatus,
    setSelectedUser,
    isDataAvailable,
  };
}

export default useUsers;
